import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { must, useDb } from './db';

/**
 * «Гуртки»: the school's clubs with their weekly times. A student joins in one tap; in primary
 * school (1–4) the request waits for a guardian, who may also enrol the child themselves.
 */
export interface ClubSlot {
  /** ISO weekday: 1 = Monday */
  weekday: number;
  startsAt: string;
  endsAt: string;
}

export interface Club {
  id: string;
  title: string;
  about: string | null;
  emoji: string | null;
  leader: string | null;
  leaderId: string | null;
  gradeFrom: number;
  gradeTo: number;
  capacity: number | null;
  room: string | null;
  slots: ClubSlot[];
  taken: number;
}

export type ClubStatus = 'active' | 'pending';

export interface ClubMembership {
  clubId: string;
  studentId: string;
  status: ClubStatus;
}

export interface RosterRow {
  studentId: string;
  name: string;
  classLabel: string | null;
  status: ClubStatus;
}

/** short names of ISO weekdays: `WEEKDAY_SHORT[1]` is Monday */
export const WEEKDAY_SHORT = ['', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Нд'] as const;
const hm = (t: string) => t.slice(0, 5);

/** «Вт, Чт · 15:00–16:00», or each day with its own time when they differ. */
export function clubTimes(slots: readonly ClubSlot[]): string {
  const sorted = [...slots].sort((a, b) => a.weekday - b.weekday);
  const times = new Set(sorted.map((s) => `${hm(s.startsAt)}–${hm(s.endsAt)}`));
  if (times.size === 1) {
    return `${sorted.map((s) => WEEKDAY_SHORT[s.weekday]).join(', ')} · ${[...times][0]}`;
  }
  return sorted
    .map((s) => `${WEEKDAY_SHORT[s.weekday]} ${hm(s.startsAt)}–${hm(s.endsAt)}`)
    .join(', ');
}

/** Free seats, or null when the club takes everyone. */
export function seatsLeft(club: Pick<Club, 'capacity' | 'taken'>): number | null {
  return club.capacity === null ? null : Math.max(0, club.capacity - club.taken);
}

export function fitsGrade(club: Pick<Club, 'gradeFrom' | 'gradeTo'>, grade: number | undefined) {
  return grade === undefined || (grade >= club.gradeFrom && grade <= club.gradeTo);
}

/** A student's clubs that meet on a weekday, by time: for the schedule. */
export function clubsOnDay(
  clubs: readonly Club[],
  memberships: readonly ClubMembership[],
  studentId: string,
  weekday: number,
) {
  const mine = new Set(
    memberships
      .filter((m) => m.studentId === studentId && m.status === 'active')
      .map((m) => m.clubId),
  );
  return clubs
    .filter((c) => mine.has(c.id))
    .flatMap((c) => c.slots.filter((s) => s.weekday === weekday).map((slot) => ({ club: c, slot })))
    .sort((a, b) => a.slot.startsAt.localeCompare(b.slot.startsAt));
}

export function useClubs(schoolId: string) {
  const db = useDb();
  return useQuery({
    queryKey: ['clubs', schoolId],
    enabled: schoolId !== '',
    queryFn: async (): Promise<Club[]> => {
      const [rows, seats] = await Promise.all([
        db
          .from('clubs')
          .select(
            `id, title, about, emoji, leader_member_id, grade_from, grade_to, capacity, room,
            leader:school_members!clubs_school_id_leader_member_id_fkey(first_name, last_name, middle_name),
            club_slots(weekday, starts_at, ends_at)`,
          )
          .eq('school_id', schoolId)
          .order('title'),
        db.rpc('club_seats', { p_school: schoolId }),
      ]);
      const taken = new Map(must(seats).map((s) => [s.club_id, s.taken]));
      return must(rows).map((r) => ({
        id: r.id,
        title: r.title,
        about: r.about,
        emoji: r.emoji,
        leader: r.leader
          ? `${r.leader.first_name} ${r.leader.middle_name ?? ''} ${r.leader.last_name}`.replace(
              /\s+/g,
              ' ',
            )
          : null,
        leaderId: r.leader_member_id,
        gradeFrom: r.grade_from,
        gradeTo: r.grade_to,
        capacity: r.capacity,
        room: r.room,
        slots: r.club_slots.map((s) => ({
          weekday: s.weekday,
          startsAt: s.starts_at,
          endsAt: s.ends_at,
        })),
        taken: taken.get(r.id) ?? 0,
      }));
    },
  });
}

/** Clubs of «my» students (myself, or my children): what the family may see. */
export function useClubMemberships(studentIds: readonly string[]) {
  const db = useDb();
  return useQuery({
    queryKey: ['club-members', [...studentIds].sort()],
    enabled: studentIds.length > 0,
    queryFn: async (): Promise<ClubMembership[]> =>
      must(
        await db
          .from('club_members')
          .select('club_id, student_member_id, status')
          .in('student_member_id', [...studentIds]),
      ).map((r) => ({ clubId: r.club_id, studentId: r.student_member_id, status: r.status })),
  });
}

/** Who is in a club: for its leader and the administration. */
export function useClubRoster(clubId: string | undefined) {
  const db = useDb();
  return useQuery({
    queryKey: ['club-roster', clubId],
    enabled: Boolean(clubId),
    queryFn: async (): Promise<RosterRow[]> =>
      must(await db.rpc('club_roster', { p_club: clubId as string })).map((r) => ({
        studentId: r.student_member_id,
        name: `${r.last_name} ${r.first_name}`,
        classLabel: r.class_label,
        status: r.status,
      })),
  });
}

export interface ClubDraft {
  title: string;
  emoji: string;
  about: string;
  leaderId: string;
  gradeFrom: number;
  gradeTo: number;
  capacity: number | null;
  room: string;
  slots: ClubSlot[];
}

export function useClubActions(schoolId: string) {
  const db = useDb();
  const client = useQueryClient();
  const invalidate = () => {
    void client.invalidateQueries({ queryKey: ['clubs'] });
    void client.invalidateQueries({ queryKey: ['club-members'] });
    void client.invalidateQueries({ queryKey: ['club-roster'] });
  };
  return {
    join: useMutation({
      mutationFn: async (a: { clubId: string; studentId: string }) =>
        must(await db.rpc('join_club', { p_club: a.clubId, p_student: a.studentId })),
      onSettled: invalidate,
    }),
    leave: useMutation({
      mutationFn: async (a: { clubId: string; studentId: string }) =>
        must(await db.rpc('leave_club', { p_club: a.clubId, p_student: a.studentId })),
      onSettled: invalidate,
    }),
    create: useMutation({
      mutationFn: async (d: ClubDraft) => {
        const id = globalThis.crypto.randomUUID();
        must(
          await db.from('clubs').insert({
            id,
            school_id: schoolId,
            title: d.title.trim(),
            emoji: d.emoji.trim() || null,
            about: d.about.trim() || null,
            leader_member_id: d.leaderId || null,
            grade_from: d.gradeFrom,
            grade_to: d.gradeTo,
            capacity: d.capacity,
            room: d.room.trim() || null,
          }),
        );
        if (d.slots.length > 0) {
          must(
            await db.from('club_slots').insert(
              d.slots.map((s) => ({
                school_id: schoolId,
                club_id: id,
                weekday: s.weekday,
                starts_at: s.startsAt,
                ends_at: s.endsAt,
              })),
            ),
          );
        }
        return id;
      },
      onSettled: invalidate,
    }),
    remove: useMutation({
      mutationFn: async (clubId: string) => must(await db.from('clubs').delete().eq('id', clubId)),
      onSettled: invalidate,
    }),
  };
}

/** The server's refusals, in words for people. */
export function clubError(error: unknown): 'full' | 'grade' | 'other' {
  // Supabase errors are plain objects with a message
  const message =
    typeof error === 'object' && error !== null && 'message' in error
      ? String((error as { message: unknown }).message)
      : String(error);
  if (/full/.test(message)) return 'full';
  if (/other classes/.test(message)) return 'grade';
  return 'other';
}
